import {
  failure,
  initial,
  isInitial,
  pending,
  RemoteData,
  remoteData,
  success,
} from "@devexperts/remote-data-ts";
import { sequenceS } from "fp-ts/lib/Apply";
import * as E from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/function";
import * as O from "fp-ts/lib/Option";
import * as R from "fp-ts/lib/Record";
import * as TE from "fp-ts/lib/TaskEither";
import * as t from "io-ts";
import { PathReporter } from "io-ts/lib/PathReporter";
import queryString from "query-string";
import React from "react";
import { ApiError, decoderError } from "./network/api";
import {
  del as del_,
  get as get_,
  Mixed,
  post as post_,
  put as put_,
  RequestError,
  Response,
} from "./network/request";

import * as RD from "@devexperts/remote-data-ts";

import humps from "lodash-humps";
import createHumps from "lodash-humps/lib/createHumps";
import snakeCase from "lodash.snakecase";

const ApplicationResponseBody = <T extends t.Mixed>(result: T) =>
  t.interface({
    errors: t.dictionary(t.string, t.array(t.string)),
    result,
  });

export {
  failure,
  fold,
  initial,
  pending,
  success,
} from "@devexperts/remote-data-ts";

export type { RemoteData } from "@devexperts/remote-data-ts";

export type ApplicationError = {
  type: string;
  e: Record<string, string>;
  __original?: ApiError;
};

export function mapFormErrors(x: ApplicationError): Record<string, string> {
  return x.e;
}

type HeadersMap = Record<string, string>;
export type Json = Mixed;

export type ApplicationResponse<A> = TE.TaskEither<
  ApplicationError,
  Response<A>
>;
export type FormResponse<A> = TE.TaskEither<
  Record<string, string>,
  Response<A>
>;

export function genericError<A>(
  type: string,
): TE.TaskEither<ApplicationError, Response<A>> {
  return TE.left({
    type,
    e: { _error: "Errore generico" },
  });
}

function mapError(x: ApiError): ApplicationError {
  return {
    type: x.type,
    e: { _error: "Errore generico" },
    __original: x,
  };
}

export function decodeJson<A>(
  json: Json,
  decoder: t.Decoder<Json, A>,
  options?: { skipSnakeCase: boolean },
): E.Either<ApplicationError, A> {
  function report<X, T extends E.Either<t.Errors, X>>(e: T) {
    if (E.isLeft(e)) {
      PathReporter.report(e).forEach(x => console.error(x));
    }
    return e;
  }
  const data = options?.skipSnakeCase ? json : humps(json);
  return pipe(
    decoder.decode(data),
    report,
    E.mapLeft(decoderError),
    E.mapLeft(mapError),
  );
}

function decode<A>(
  response: Response<Json>,
  decoder: t.Decoder<Json, A>,
  options?: { skipSnakeCase: boolean },
): E.Either<ApplicationError, Response<A>> {
  return pipe(
    decodeJson(response.body, decoder, options),
    E.map(body => ({ ...response, body })),
  );
}

function handleApiResponse<A>(
  response: TE.TaskEither<RequestError, Response<Mixed>>,
  decoder?: t.Decoder<Json, A>,
): TE.TaskEither<ApplicationError, Response<A>> {
  return pipe(
    response,
    TE.mapLeft(mapError),
    TE.chain(resp => {
      const result = decode(resp, ApplicationResponseBody(t.any));
      return TE.fromEither(
        pipe(
          result,
          E.chain(data => {
            if (data.status >= 200 && data.status < 300) {
              const body = data.body.result;
              return decode({ ...data, body }, decoder || t.any);
            } else {
              return E.left<ApplicationError, Response<A>>({
                type: "BadResponse",
                e: pipe(
                  data.body.errors,
                  R.map(e => e[0]),
                ),
              });
            }
          }),
        ),
      );
    }),
  );
}

export function handleApiResponseSkipCase<A>(
  response: TE.TaskEither<RequestError, Response<Mixed>>,
  decoder?: t.Decoder<Json, A>,
): TE.TaskEither<ApplicationError, Response<A>> {
  return pipe(
    response,
    TE.mapLeft(mapError),
    TE.chain(resp => {
      console.log(resp);
      const result = decode(resp, ApplicationResponseBody(t.any), {
        skipSnakeCase: true,
      });
      return TE.fromEither(
        pipe(
          result,
          E.chain(data => {
            if (data.status >= 200 && data.status < 300) {
              const body = data.body.result;
              return decode({ ...data, body }, decoder || t.any, {
                skipSnakeCase: true,
              });
            } else {
              return E.left<ApplicationError, Response<A>>({
                type: "BadResponse",
                e: pipe(
                  data.body.errors,
                  R.map(e => e[0]),
                ),
              });
            }
          }),
        ),
      );
    }),
  );
}

export function handleApiResponseLowLevel<A>(
  response: TE.TaskEither<RequestError, Response<Mixed>>,
  decoder?: t.Decoder<Json, A>,
): TE.TaskEither<ApplicationError, Response<A>> {
  return pipe(
    response,
    TE.mapLeft(mapError),
    TE.chain(resp => {
      const result = decode(resp, decoder || t.any);
      return TE.fromEither(
        pipe(
          result,
          E.chain(data => {
            if (data.status >= 200 && data.status < 300) {
              return E.right(data);
            } else {
              return E.left<ApplicationError, Response<A>>({
                type: "BadResponse",
                e: {},
              });
            }
          }),
        ),
      );
    }),
  );
}

interface ApiOptions<A> extends RequestInit {
  headers?: HeadersMap;
  decoder?: t.Decoder<Json, A>;
  middleware?: typeof handleApiResponse | typeof handleApiResponseLowLevel;
  reporter?: (error: t.ValidationError) => unknown;
  data?: Record<string, unknown>;
  params?: Record<string, string | (string | null)[] | null | undefined>;
  host?: string;
  skipHumps?: boolean;
}

function buildUrl<A>(url: string, options: ApiOptions<A>) {
  const host = options.host || process.env.GROMIA_API_HOST || "";
  const dict = t.dictionary(t.string, t.unknown);
  const search = pipe(
    dict.decode(options.params),
    E.map(createHumps(snakeCase)),
    E.chain(x => dict.decode(x)),
    E.map(d => queryString.stringify(d, { arrayFormat: "bracket" })),
    E.map(x => `?${x}`),
  );
  return `${host}${url}${
    pipe(
      search,
      E.getOrElse(() => ""),
    )
  }`;
}

function report<A>(
  te: TE.TaskEither<ApplicationError, Response<A>>,
  options: ApiOptions<A>,
): TE.TaskEither<ApplicationError, Response<A>> {
  return pipe(
    te,
    TE.mapLeft(err => {
      if (
        err.__original
        && err.__original.type === "DecoderError"
        && options.reporter
      ) {
        console.error(
          JSON.stringify(err.__original.errors.map(options.reporter), null, 2),
        );
      }
      return err;
    }),
  );
}

export function getL<A>(
  url: string,
  token: TE.TaskEither<unknown, { Authorization: string }>,
  options: ApiOptions<A>,
): TE.TaskEither<ApplicationError, Response<A>> {
  return pipe(
    token,
    TE.mapLeft(() => ({ type: "TokenGetError", e: {} })),
    TE.chain(o =>
      get(url, { ...options, headers: { ...options.headers, ...o } })
    ),
  );
}

export function postL<A>(
  url: string,
  token: TE.TaskEither<unknown, { Authorization: string }>,
  options: ApiOptions<A>,
): TE.TaskEither<ApplicationError, Response<A>> {
  return pipe(
    token,
    TE.mapLeft(() => ({ type: "TokenGetError", e: {} })),
    TE.chain(o =>
      post(url, { ...options, headers: { ...options.headers, ...o } })
    ),
  );
}

export function putL<A>(
  url: string,
  token: TE.TaskEither<unknown, { Authorization: string }>,
  options: ApiOptions<A>,
): TE.TaskEither<ApplicationError, Response<A>> {
  return pipe(
    token,
    TE.mapLeft(() => ({ type: "TokenGetError", e: {} })),
    TE.map(x => {
      console.log(x);
      return x;
    }),
    TE.chain(o =>
      put(url, {
        ...options,
        headers: {
          ...options.headers,
          ...o,
        },
        // put returns 204
        decoder: options.decoder || t.any,
        middleware: handleApiResponseLowLevel,
      })
    ),
  );
}

export function delL<A>(
  url: string,
  token: TE.TaskEither<unknown, { Authorization: string }>,
  options: ApiOptions<A>,
): TE.TaskEither<ApplicationError, Response<A>> {
  return pipe(
    token,
    TE.mapLeft(() => ({ type: "TokenGetError", e: {} })),
    TE.map(x => {
      console.log(x);
      return x;
    }),
    TE.chain(o =>
      del(url, {
        ...options,
        headers: {
          ...options.headers,
          ...o,
        },
        // put returns 204
        decoder: options.decoder || t.any,
        middleware: handleApiResponseLowLevel,
      })
    ),
  );
}

export function get<A>(
  url: string,
  options: ApiOptions<A>,
): TE.TaskEither<ApplicationError, Response<A>> {
  const { decoder, ...opts } = options;
  const middleware = opts.middleware || handleApiResponse;
  return report(
    middleware(get_(buildUrl(url, options), opts), decoder),
    options,
  );
}

export function post<A>(
  url: string,
  options: ApiOptions<A>,
): TE.TaskEither<ApplicationError, Response<A>> {
  const { decoder, ...opts } = options;
  const middleware = opts.middleware || handleApiResponse;
  const hump = createHumps(snakeCase);
  return report(
    middleware(
      post_(buildUrl(url, options), {
        ...opts,
        headers: {
          Accept: "application/json",
          "Content-type": "application/json",
          ...opts.headers,
        },
        body: pipe(
          options.data,
          O.fromNullable,
          O.map(hump),
          O.map(JSON.stringify),
          O.toUndefined,
        ),
      }),
      decoder,
    ),
    options,
  );
}

export function put<A>(
  url: string,
  options: ApiOptions<A>,
): TE.TaskEither<ApplicationError, Response<A>> {
  const { decoder, ...opts } = options;
  const middleware = opts.middleware || handleApiResponse;
  const hump = createHumps(snakeCase);
  return report(
    middleware(
      put_(buildUrl(url, options), {
        ...opts,
        headers: {
          Accept: "application/json",
          "Content-type": "application/json",
          ...opts.headers,
        },
        body: pipe(
          options.data,
          O.fromNullable,
          O.map(hump),
          O.map(JSON.stringify),
          O.toUndefined,
        ),
      }),
      decoder,
    ),
    options,
  );
}

export function del<A>(
  url: string,
  options: ApiOptions<A>,
): TE.TaskEither<ApplicationError, Response<A>> {
  const { decoder, ...opts } = options;
  const middleware = opts.middleware || handleApiResponse;
  const hump = createHumps(snakeCase);
  return report(
    middleware(
      del_(buildUrl(url, options), {
        ...opts,
        headers: {
          Accept: "application/json",
          "Content-type": "application/json",
          ...opts.headers,
        },
        body: pipe(
          options.data,
          O.fromNullable,
          O.map(hump),
          O.map(JSON.stringify),
          O.toUndefined,
        ),
      }),
      decoder,
    ),
    options,
  );
}

export function perform<L, R>(
  fetcher: TE.TaskEither<L, Response<R>>,
  onFailure: (l: L) => void,
  onSuccess: (r: R) => void,
) {
  fetcher().then(e => {
    if (E.isLeft(e)) {
      console.error("RemoteData", "FetcherError", e);
    }

    if (E.isRight(e)) {
      onSuccess(e.right.body);
    }

    if (E.isLeft(e)) {
      onFailure(e.left);
    }
  });
}

export function useAction<L, R>() {
  const [loading, setLoading] = React.useState(false);

  function perform2(
    fetcher: TE.TaskEither<L, Response<R>>,
    onSuccess: (r: R) => void,
    onFailure?: (l: L) => void,
  ) {
    setLoading(true);
    perform(
      fetcher,
      l => {
        setLoading(false);
        console.error("error performing network action", l);
        if (onFailure) {
          onFailure(l);
        }
      },
      r => {
        setLoading(false);
        onSuccess(r);
      },
    );
  }

  return {
    perform: perform2,
    loading,
    buttonProps: {
      disabled: loading,
      loading,
    },
  };
}

export function useActionWithStage<L, R>(state: R) {
  const a = useAction<L, R>();
  const [stage, setStage] = React.useState(state);
  const [store, setStore] = React.useState(state);

  function perform(fetcher: TE.TaskEither<L, Response<R>>) {
    a.perform(fetcher, () => {
      setStore(stage);
    });
  }

  return {
    store,
    stage,
    setStage,
    perform,
    loading: a.loading,
    buttonProps: a.buttonProps,
  };
}

export function useRemoteData<L, R>(
  fetcher: TE.TaskEither<L, Response<R>>,
  watches: unknown[] = [],
): RemoteData<L, R> {
  const [state, setState] = React.useState<RemoteData<L, R>>(initial);
  React.useEffect(() => {
    setState(pending);
    fetcher().then(e => {
      if (E.isLeft(e)) {
        console.error("RemoteData", "FetcherError", e);
      }
      const s = pipe(
        e,
        E.fold<L, Response<R>, RemoteData<L, R>>(
          x => failure(x),
          x => success(x.body),
        ),
      );
      setState(s);
    });
  }, watches);
  return state;
}

export function useRemoteDataMutable<L, R>(
  fetcher: TE.TaskEither<L, Response<R>>,
  watches: unknown[] = [],
): { data: RemoteData<L, R>; put: (x: R) => void } {
  const [state, setState] = React.useState<RemoteData<L, R>>(initial);
  React.useEffect(() => {
    setState(pending);
    fetcher().then(e => {
      if (E.isLeft(e)) {
        console.error("RemoteData", "FetcherError", e);
      }
      const s = pipe(
        e,
        E.fold<L, Response<R>, RemoteData<L, R>>(
          x => failure(x),
          x => success(x.body),
        ),
      );
      setState(s);
    });
  }, watches);

  function put(r: R) {
    setState(RD.success(r));
  }

  return { data: state, put };
}

export const responseToBody = <R>(x: Response<R>) => x.body;
export const responseToBodyAndHeaders = <R>(x: Response<R>) => ({
  body: x.body,
  headers: x.headers,
});

export function useRemoteDataOptionWithParser<L, R, I>(
  fetcher: O.Option<TE.TaskEither<L, Response<R>>>,
  parser: (r: Response<R>) => I,
  watches: unknown[] = [],
  data: O.Option<E.Either<L, I>> = O.none,
): RemoteData<L, I> {
  const [state, setState] = React.useState<RemoteData<L, I>>(
    pipe(
      data,
      O.fold(() => RD.initial, RD.fromEither),
    ),
  );

  if (O.isSome(data)) {
    return state;
  }

  React.useEffect(() => {
    if (O.isNone(fetcher) && !isInitial(state)) {
      setState(initial);
      return;
    }
    pipe(
      fetcher,
      O.map((f: TE.TaskEither<L, Response<R>>) => {
        setState(pending);
        f().then(e => {
          if (E.isLeft(e)) {
            console.error("RemoteData", "FetcherError", e);
          }
          const s = pipe(
            e,
            E.fold<L, Response<R>, RemoteData<L, I>>(
              x => failure(x),
              x => success(parser(x)),
            ),
          );
          setState(s);
        });
      }),
    );
  }, watches);
  return state;
}

export function useRemoteDataOption<L, R>(
  fetcher: O.Option<TE.TaskEither<L, Response<R>>>,
  watches: unknown[] = [],
  data: O.Option<E.Either<L, R>> = O.none,
) {
  return useRemoteDataOptionWithParser(fetcher, responseToBody, watches, data);
}

export const sequence = sequenceS(remoteData);

export type RemoteDataResponse<T> = RemoteData<ApplicationError, T>;
